import {
  CheckCircleFilled,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DoubleRightOutlined,
} from '@ant-design/icons';
import React from 'react';
import { createGlobalStyle } from 'styled-components';

export type TransactionsFileStateTab = 'CAPTURED' | 'REVIEWED' | 'POSTED';

export type TransactionsFileStateStepperProps = {
  activeTab: TransactionsFileStateTab;
  counts: { CAPTURED: number; REVIEWED: number };
  onTabChange: (tab: TransactionsFileStateTab) => void;
  ariaLabel: string;
  pendingLabel: string;
  reviewedLabel: string;
  postedLabel: string;
  showPosted?: boolean;
  formatCount?: (n: number) => string;
  /** Extra class on the tablist (e.g. PWA orb layout). */
  className?: string;
};

const STEP_ORDER: TransactionsFileStateTab[] = [
  'CAPTURED',
  'REVIEWED',
  'POSTED',
];

/** Injected by the stepper; also exported so the GT button gallery can preview the class. */
export const TransactionsFileStateStepperStyles = createGlobalStyle`
  button.transactions-file-state-tab {
    appearance: none;
    -webkit-appearance: none;
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex-shrink: 0;
    box-sizing: border-box;
    min-height: 40px;
    padding: 6px 16px;
    border-radius: 999px;
    border: 1px solid var(--color-transaction-stepper-border, #dbeae3);
    background: var(--color-transaction-stepper-bg, #ffffff);
    box-shadow: 0 1px 2px rgba(4, 42, 42, 0.04);
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: normal;
    white-space: nowrap;
    transition: background-color 0.15s ease, border-color 0.15s ease,
      box-shadow 0.15s ease;
    color: inherit;
  }

  button.transactions-file-state-tab:not(:disabled) {
    cursor: pointer;
  }

  button.transactions-file-state-tab:disabled {
    cursor: not-allowed;
    opacity: 0.68;
    border-color: var(--color-transaction-stepper-disabled-border, #e8ecee);
    background: var(--color-transaction-stepper-disabled-bg, #f8fbfb);
    box-shadow: none;
  }

  button.transactions-file-state-tab[aria-selected='true']:not(:disabled) {
    border-color: var(--color-primary, #41aaaf);
    background: var(--color-transaction-stepper-selected-bg, #e9f6f7);
    box-shadow: 0 0 0 1px color-mix(
      in srgb,
      var(--color-primary, #41aaaf) 20%,
      transparent
    );
  }

  button.transactions-file-state-tab:not(:disabled):not([aria-selected='true']):hover {
    border-color: var(--color-transaction-stepper-hover-border, #94c9cc);
    background: var(--color-transaction-stepper-hover-bg, #f3fbfc);
  }

  button.transactions-file-state-tab:not(:disabled):active {
    transform: none;
    box-shadow: 0 1px 2px rgba(4, 42, 42, 0.04);
  }

  button.transactions-file-state-tab:focus-visible {
    outline: 2px solid var(--color-primary, #41aaaf);
    outline-offset: 2px;
  }

  .transactions-file-state-stepper {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 0;
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 2px 0;
  }

  .transactions-file-state-stepper__step-content {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .transactions-file-state-stepper__orb {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    line-height: 1;
  }

  .transactions-file-state-stepper__label {
    font-size: 14px;
    line-height: 1.2;
  }

  .transactions-file-state-stepper__count {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
  }

  .transactions-file-state-stepper__connector {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 44px;
    width: 44px;
    color: var(--color-text-muted, #5a6a6a);
    font-size: 20px;
    line-height: 1;
    user-select: none;
    opacity: 0.95;
  }

  .transactions-file-state-stepper__connector .anticon {
    font-size: 20px;
  }

  .transactions-file-state-stepper__connector--completed {
    color: var(--color-primary, #41aaaf);
    opacity: 1;
  }

  .transactions-file-state-stepper__step .capture-workflow-metric__icon,
  .transactions-file-state-stepper__step .entity-capture-backlog__icon {
    font-size: 16px;
    line-height: 1;
    flex-shrink: 0;
  }

  .transactions-file-state-stepper__step--pending-review
    .capture-workflow-metric__icon--pending-review,
  .transactions-file-state-stepper__step--pending-review
    .entity-capture-backlog__icon--pending-review {
    color: var(--color-transaction-pending, #dba800);
  }

  .transactions-file-state-stepper__step--ready-to-post
    .capture-workflow-metric__icon--ready-to-post,
  .transactions-file-state-stepper__step--ready-to-post
    .entity-capture-backlog__icon--ready-to-post {
    color: var(--color-primary, #41aaaf);
  }

  .transactions-file-state-stepper__step--posted
    .capture-workflow-metric__icon--posted,
  .transactions-file-state-stepper__step--posted
    .entity-capture-backlog__icon--posted {
    color: var(--color-transaction-posted, #3c7edb);
  }

  button.transactions-file-state-tab.transactions-file-state-stepper__step--selected.transactions-file-state-stepper__step--pending-review {
    border-color: var(--color-transaction-pending, #dba800);
    background: var(--color-transaction-pending-bg, #fffef5);
    box-shadow: 0 0 0 1px color-mix(
      in srgb,
      var(--color-transaction-pending, #dba800) 18%,
      transparent
    );
  }

  button.transactions-file-state-tab.transactions-file-state-stepper__step--selected.transactions-file-state-stepper__step--ready-to-post {
    border-color: var(--color-primary, #41aaaf);
    background: var(--color-transaction-stepper-selected-bg, #e9f6f7);
    box-shadow: 0 0 0 1px color-mix(
      in srgb,
      var(--color-primary, #41aaaf) 20%,
      transparent
    );
  }

  button.transactions-file-state-tab.transactions-file-state-stepper__step--selected.transactions-file-state-stepper__step--posted {
    border-color: var(--color-transaction-posted, #3c7edb);
    background: var(--color-transaction-posted-bg, #f3f8fd);
    box-shadow: 0 0 0 1px color-mix(
      in srgb,
      var(--color-transaction-posted, #3c7edb) 18%,
      transparent
    );
  }

  /* Selected hover: fill only. Border and ring stay on the selected tone. */
  button.transactions-file-state-tab[aria-selected='true']:not(:disabled):hover {
    background: var(--color-transaction-stepper-selected-hover-bg, #dff0f2);
  }

  .transactions-file-state-stepper__step--completed:not(
      .transactions-file-state-stepper__step--selected
    ) {
    border-color: var(--color-transaction-stepper-completed-border, #c9e8ea);
    background: var(--color-transaction-stepper-completed-bg, #f8fcfc);
  }

  .transactions-file-state-stepper__step--completed:not(
      .transactions-file-state-stepper__step--selected
    )
    .transactions-file-state-stepper__label,
  .transactions-file-state-stepper__step--completed:not(
      .transactions-file-state-stepper__step--selected
    )
    .transactions-file-state-stepper__count {
    color: var(--color-text-muted, #5a6a6a);
  }
`;

export const TransactionsFileStateStepper: React.FC<
  TransactionsFileStateStepperProps
> = ({
  activeTab,
  showPosted = true,
  counts,
  onTabChange,
  ariaLabel,
  pendingLabel,
  reviewedLabel,
  postedLabel,
  formatCount,
  className,
}) => {
  const steps: Array<{
    id: TransactionsFileStateTab;
    label: string;
    tone: 'pending-review' | 'ready-to-post' | 'posted';
    icon: React.ReactNode;
    count?: number;
  }> = [
    {
      id: 'CAPTURED',
      label: pendingLabel,
      tone: 'pending-review',
      icon: (
        <ClockCircleOutlined
          className="entity-capture-backlog__icon entity-capture-backlog__icon--pending-review capture-workflow-metric__icon capture-workflow-metric__icon--pending-review"
          aria-hidden
        />
      ),
      count: counts.CAPTURED,
    },
    {
      id: 'REVIEWED',
      label: reviewedLabel,
      tone: 'ready-to-post',
      icon: (
        <CheckCircleOutlined
          className="entity-capture-backlog__icon entity-capture-backlog__icon--ready-to-post capture-workflow-metric__icon capture-workflow-metric__icon--ready-to-post"
          aria-hidden
        />
      ),
      count: counts.REVIEWED,
    },
    ...(showPosted
      ? [
          {
            id: 'POSTED' as const,
            label: postedLabel,
            tone: 'posted' as const,
            icon: (
              <CheckCircleFilled
                className="capture-workflow-metric__icon capture-workflow-metric__icon--posted"
                aria-hidden
              />
            ),
          },
        ]
      : []),
  ];

  const activeIndex = STEP_ORDER.indexOf(activeTab);

  return (
    <>
      <TransactionsFileStateStepperStyles />
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={['transactions-file-state-stepper', className]
          .filter(Boolean)
          .join(' ')}
        data-step-count={steps.length}
      >
        {steps.map((step, index) => {
          const selected = activeTab === step.id;
          const completed = activeIndex > index;
          const isLast = index === steps.length - 1;
          const countLabel =
            step.count != null
              ? formatCount
                ? formatCount(step.count)
                : String(step.count)
              : null;

          return (
            <React.Fragment key={step.id}>
              <button
                type="button"
                role="tab"
                aria-selected={selected}
                data-completed={completed ? 'true' : 'false'}
                {...(step.id === 'REVIEWED'
                  ? { 'data-tour-ready-to-post-tab': true }
                  : step.id === 'POSTED'
                    ? { 'data-tour-posted-tab': true }
                    : {})}
                className={[
                  'transactions-file-state-tab',
                  'transactions-file-state-stepper__step',
                  `transactions-file-state-stepper__step--${step.tone}`,
                  selected
                    ? 'transactions-file-state-stepper__step--selected'
                    : '',
                  completed
                    ? 'transactions-file-state-stepper__step--completed'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => onTabChange(step.id)}
              >
                <span className="transactions-file-state-stepper__step-content">
                  <span
                    className="transactions-file-state-stepper__orb"
                    aria-hidden
                  >
                    {step.icon}
                  </span>
                  <span className="transactions-file-state-stepper__label">
                    {step.label}
                  </span>
                  {countLabel != null ? (
                    <span className="transactions-file-state-stepper__count">
                      ({countLabel})
                    </span>
                  ) : null}
                </span>
              </button>
              {!isLast ? (
                <span
                  aria-hidden
                  className={[
                    'transactions-file-state-stepper__connector',
                    completed
                      ? 'transactions-file-state-stepper__connector--completed'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <DoubleRightOutlined />
                </span>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};
